/**
 * gifPoster.js — extract a still POSTER frame (the GIF's first frame) as a data
 * URL, so a GIF Segment fill has a real still image for every surface that must
 * NOT animate (EW-003 u1b):
 *   · the closed wedge <pattern> (SVG <image> freezes GIFs anyway, EW-009 — a
 *     poster makes the still deterministic instead of browser-dependent),
 *   · a reduced-motion viewer (the open frame holds the poster),
 *   · the closed-state "GIF" chip preview.
 *
 * The DOM draw (Image → canvas → toDataURL) is isolated behind an injectable
 * `deps` seam so the pure logic (dimension capping, error handling) is unit-
 * testable without a real browser. In the app the defaults use the real DOM.
 *
 * NON-DESTRUCTIVE: the poster is stored ALONGSIDE the original GIF url; the GIF
 * itself is never modified. If extraction fails (CORS-tainted canvas, decode
 * error, no DOM) we resolve to null and the caller simply falls back to the GIF
 * url as its own still — never throwing, never blocking the fill.
 */

/** Cap the poster raster so a huge GIF frame does not bloat the settings blob. */
export const MAX_POSTER_EDGE = 512;

/**
 * Compute the poster canvas size for a source frame, capping the longest edge to
 * MAX_POSTER_EDGE while preserving aspect ratio. Pure.
 *
 * @param {number} w natural width
 * @param {number} h natural height
 * @returns {{width:number,height:number}} integer canvas dims (>=1)
 */
export const posterCanvasSize = (w, h) => {
  const sw = Math.max(1, Math.floor(Number(w) || 0) || 1);
  const sh = Math.max(1, Math.floor(Number(h) || 0) || 1);
  const longest = Math.max(sw, sh);
  if (longest <= MAX_POSTER_EDGE) return { width: sw, height: sh };
  const scale = MAX_POSTER_EDGE / longest;
  return {
    width: Math.max(1, Math.round(sw * scale)),
    height: Math.max(1, Math.round(sh * scale)),
  };
};

/**
 * Draw an already-loaded image-like onto a fresh canvas and return its data URL.
 * Pure w.r.t. the injected canvas factory (no global document needed). Returns
 * null on any failure.
 *
 * @param {{naturalWidth?:number,naturalHeight?:number,width?:number,height?:number}} img
 * @param {() => HTMLCanvasElement} createCanvas factory for a canvas element
 * @param {string} [mime] output mime (default image/png)
 * @returns {string|null} data URL or null
 */
export const posterFromImage = (img, createCanvas, mime = "image/png") => {
  try {
    if (!img || typeof createCanvas !== "function") return null;
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    const { width, height } = posterCanvasSize(w, h);
    const canvas = createCanvas();
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext && canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL(mime);
  } catch (_e) {
    return null;
  }
};

/**
 * Load a GIF url and extract its first-frame poster as a data URL. Resolves to
 * null (never rejects) on any failure so the caller can safely fall back to the
 * GIF url as its own still.
 *
 * @param {string} url the GIF url
 * @param {object} [deps] injectable seam for tests
 * @param {() => any} [deps.createImage] make an Image()
 * @param {() => any} [deps.createCanvas] make a <canvas>
 * @param {string} [deps.mime]
 * @param {typeof fetch} [deps.fetch] CORS retry fetch (default: global fetch)
 * @param {(b: Blob) => string} [deps.createObjectURL]
 * @param {(u: string) => void} [deps.revokeObjectURL]
 * @returns {Promise<string|null>}
 */
export const extractGifPoster = (url, deps = {}) => {
  const createImage =
    deps.createImage ||
    (typeof Image !== "undefined" ? () => new Image() : null);
  const createCanvas =
    deps.createCanvas ||
    (typeof document !== "undefined"
      ? () => document.createElement("canvas")
      : null);

  if (!url || !createImage || !createCanvas) return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    const done = (val) => {
      if (settled) return;
      settled = true;
      resolve(val);
    };
    let img;
    try {
      img = createImage();
    } catch (_e) {
      return done(null);
    }
    // Request an untainted canvas where the host allows it (falls back to null on
    // a cross-origin taint, which is caught by posterFromImage's try/catch).
    try {
      img.crossOrigin = "anonymous";
    } catch (_e) {
      /* some hosts throw on the setter — ignore, extraction may still work */
    }
    img.onload = () => done(posterFromImage(img, createCanvas, deps.mime));
    // T22.145: a CORS <img> load of an S3 gif FAILS once the page has already
    // shown that gif through a plain (no-cors) <image>/<img> — S3 answers a
    // request without an Origin header with no Access-Control-Allow-Origin and
    // no `Vary: Origin`, so Chromium serves that cached response to our
    // crossOrigin request and blocks it (measured live, web-stg View Wheel
    // main.f3868900: "blocked by CORS policy", while the same url with a
    // cache-busting query loaded fine). The poster then came back null and the
    // wheel painted the ANIMATING gif under prefers-reduced-motion / the
    // low-memory hover fallback. Retry once over fetch with the HTTP cache
    // bypassed (the request carries Origin, S3 answers with ACAO), and draw
    // the bytes from a same-origin blob: url — the gif url itself is untouched
    // (safe for signed urls).
    img.onerror = () => {
      if (settled) return;
      extractViaCorsFetch(url, createImage, createCanvas, deps).then(done, () => done(null));
    };
    // Guard against a never-firing load (offline / hung request).
    if (typeof setTimeout === "function") {
      setTimeout(() => done(null), deps.timeoutMs || 8000);
    }
    try {
      img.src = url;
      // Cached images may already be complete before onload is attached.
      if (img.complete && (img.naturalWidth || img.width)) {
        done(posterFromImage(img, createCanvas, deps.mime));
      }
    } catch (_e) {
      done(null);
    }
  });
};

/**
 * Fallback for extractGifPoster: fetch the gif with the HTTP cache bypassed,
 * then draw its first frame from a blob: url. Resolves to null on any failure.
 */
const extractViaCorsFetch = async (url, createImage, createCanvas, deps) => {
  const doFetch = deps.fetch || (typeof fetch === "function" ? fetch : null);
  const URLApi = typeof URL !== "undefined" ? URL : null;
  const createObjectURL =
    deps.createObjectURL || (URLApi && URLApi.createObjectURL && ((b) => URLApi.createObjectURL(b)));
  const revokeObjectURL =
    deps.revokeObjectURL || (URLApi && URLApi.revokeObjectURL && ((u) => URLApi.revokeObjectURL(u)));
  if (!doFetch || !createObjectURL || /^(data|blob):/i.test(url)) return null;
  const res = await doFetch(url, { mode: "cors", cache: "no-store", credentials: "omit" });
  if (!res || !res.ok) return null;
  const blobUrl = createObjectURL(await res.blob());
  try {
    return await new Promise((resolve) => {
      const img = createImage();
      img.onload = () => resolve(posterFromImage(img, createCanvas, deps.mime));
      img.onerror = () => resolve(null);
      img.src = blobUrl;
    });
  } finally {
    if (revokeObjectURL) revokeObjectURL(blobUrl);
  }
};

const gifPoster = { posterCanvasSize, posterFromImage, extractGifPoster, MAX_POSTER_EDGE };
export default gifPoster;
